import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Member } from "Models/members/member";
import { MemberStaffWithoutTimezoneId } from "Models/members/member.staff";
import { useTranslation } from "react-i18next";
import MembersService from "Services/members.service";
import TimezoneService from "Services/timezone.service";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { Branch } from "../models/branch";
import { Fleet, IUploadCentreCreateFleetList } from "../models/fleet";
import { IUploadCentreCreateMaterialList } from "../models/material";
import { IUploadCentreCreateServiceList, Service as ServiceModel } from "../models/service";
import { ServiceBooking } from "../models/service.booking";
import { IUploadCentreCreateStaffList, Staff } from "../models/staff";
import { UploadCentreResponse } from "../models/upload.centre";
import { Warehouse } from "../models/warehouse";
import BranchService from "../services/branch.service";
import FleetService from "../services/fleet.service";
import FleetTypeService from "../services/fleet.type.service";
import MaterialsService from "../services/materials.service";
import Service from "../services/service.booking.service";
import StaffService from "../services/staff.service";
import UploadCentreService from "../services/upload.centre.service";
import WarehouseService from "../services/warehouse.service";
import { checkError } from "../util/checkError";
import { Status } from "../util/enums";
import {
  FleetTypeKeys,
  MaterialsKeys,
  ServicesKeys,
  UploadCentreKeys,
  WarehouseKeys,
} from "../util/queries";

interface IMutateUpdateTemplateList<T> {
  labelId: number;
  data: T[];
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 5 * 60 * 60 * 1000; // 5 horas

export type IWarehouseCodeList = Pick<Warehouse, "code" | "id">;

export const useUploadCentreAction = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [branchList, setBranchList] = useState<Branch[]>([]);

  const getUploadItemsStatus = () => {
    return useQuery({
      queryKey: UploadCentreKeys.getAll([currentUser?.companyId]),
      queryFn: async () => {
        const response = await UploadCentreService.getItems();

        if (response && response?.data) return response.data;

        return {} as UploadCentreResponse;
      },
      enabled: !!(!!currentUser?.companyId && !!currentUser?.id),
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };

  const {
    data: uploadItemsStatus,
    isFetching: isUploadCentreItemsStatusLoading,
    refetch: fetchUploadCentreItemsStatus,
  } = getUploadItemsStatus();

  const { data: fleetTypes, isFetching: isFleetTypesLoading } = useQuery({
    queryKey: FleetTypeKeys.getAll([currentUser?.companyId]),
    queryFn: async () => {
      const response = await FleetTypeService.getAll();

      if (response && response?.data?.length > 0) return response.data;

      return [];
    },
    enabled: !!(!!currentUser?.companyId && !!currentUser?.id),
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const getServicesCodes = () => {
    const attributes = ["id", "code"];

    return useQuery({
      queryKey: ServicesKeys.getAll([currentUser?.companyId, attributes]),
      queryFn: async () => {
        const response = await Service.getAll({
          companyId: currentUser?.companyId,
          attributes: JSON.stringify(attributes),
        });

        if (response && response?.data?.length > 0) return response.data;

        return [];
      },
      enabled: !!(!!currentUser?.companyId && !!currentUser?.id),
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };
  const {
    data: servicesCodes,
    isFetching: isServicesCodesLoading,
    refetch: reloadServicesList,
  } = getServicesCodes();

  const getMaterialCodes = () => {
    const attributes = ["id", "code"];

    return useQuery({
      queryKey: MaterialsKeys.getAll([currentUser?.companyId, attributes]),
      queryFn: async () => {
        const response = await MaterialsService.getAll({
          companyId: currentUser?.companyId,
          attributes: JSON.stringify(attributes),
        });

        if (response && response?.data?.length > 0) return response.data;

        return [];
      },
      enabled: !!(!!currentUser?.companyId && !!currentUser?.id),
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };

  const {
    data: materialsCodes,
    isFetching: isMaterialsCodesLoading,
    refetch: reloadMaterialsList,
  } = getMaterialCodes();

  const getWarehouseList = () => {
    const includeBranches = {
      association: "branches",
      attributes: ["id", "code"],
    };

    const attributes = ["id", "code"];

    return useQuery({
      queryKey: WarehouseKeys.getAll([currentUser?.companyId, attributes, includeBranches]),
      queryFn: async () => {
        const response = await WarehouseService.getAll({
          companyId: currentUser?.companyId,
          include: JSON.stringify([includeBranches]),
          attributes: JSON.stringify(attributes),
        });

        if (response && response?.data?.length > 0) return response.data;

        return [];
      },
      enabled: !!currentUser?.id,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };
  const { data: warehouseList, isFetching: isWarehouseListLoading } = getWarehouseList();

  const { isFetching: isBranchListLoading } = useQuery(
    ["branches.list", currentUser?.companyId],
    () =>
      BranchService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        attributes: JSON.stringify(["id", "code"]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response) => {
        if (response && response.data) setBranchList(response.data);
      },
    }
  );

  const { mutate: mutateUpdateFleetList, isLoading: isUpdateFleetListLoading } = useMutation(
    ["fleet.csv.list.post"],
    async ({ labelId, data }: IMutateUpdateTemplateList<IUploadCentreCreateFleetList>) => {
      const response = await FleetService.postCSV(data);

      await UploadCentreService.postItemId(labelId);

      return response;
    },
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response) => {
        if (response && response.data)
          NotificationToast.success(translate("theFilesWereUploadedAndUpdatedSuccesfully"));
      },
    }
  );

  const { mutate: mutateUpdateMaterialList, isLoading: isUpdateMaterialListLoading } = useMutation(
    ["material.csv.list.post"],
    async ({ labelId, data }: IMutateUpdateTemplateList<IUploadCentreCreateMaterialList>) => {
      const response = await MaterialsService.postCSV(data);

      await UploadCentreService.postItemId(labelId);

      return response;
    },
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response) => {
        if (response && response.data)
          NotificationToast.success(translate("theFilesWereUploadedAndUpdatedSuccesfully"));
      },
    }
  );

  const { mutate: mutateUpdateStaffList, isLoading: isUpdateStaffListLoading } = useMutation(
    ["material.csv.list.post"],
    async ({ labelId, data }: IMutateUpdateTemplateList<IUploadCentreCreateStaffList>) => {
      const response = await StaffService.postCSV(data);

      await UploadCentreService.postItemId(labelId);

      return response;
    },
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response) => {
        if (response && response.data)
          NotificationToast.success(translate("theFilesWereUploadedAndUpdatedSuccesfully"));
      },
    }
  );

  const { mutate: mutateUpdateMemberList, isLoading: isUpdateMemberListLoading } = useMutation(
    ["material.csv.list.post"],
    async ({ labelId, data }: IMutateUpdateTemplateList<MemberStaffWithoutTimezoneId>) => {
      const response = await MembersService.postCSV(data);

      await UploadCentreService.postItemId(labelId);

      return response;
    },
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response) => {
        if (response && response.data)
          NotificationToast.success(translate("theFilesWereUploadedAndUpdatedSuccesfully"));
      },
    }
  );

  const { mutate: mutateUpdateServiceList, isLoading: isUpdateServiceListLoading } = useMutation(
    ["service.csv.list.post"],
    async ({ labelId, data }: IMutateUpdateTemplateList<IUploadCentreCreateServiceList>) => {
      const response = await Service.postCSV(data);

      await UploadCentreService.postItemId(labelId);

      return response;
    },
    {
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
      onSuccess: (response) => {
        if (response && response.data)
          NotificationToast.success(translate("theFilesWereUploadedAndUpdatedSuccesfully"));
      },
    }
  );

  const updateFleetList = (
    fleets: IUploadCentreCreateFleetList[],
    labelId: number,
    callback: (fleets: Fleet[]) => void
  ) => {
    return mutateUpdateFleetList(
      { labelId, data: fleets },
      {
        onSuccess: ({ data }) => {
          callback(data);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateMaterialList = (
    material: IUploadCentreCreateMaterialList[],
    labelId: number,
    callback: (material: ServiceBooking[]) => void
  ) => {
    return mutateUpdateMaterialList(
      { labelId, data: material },
      {
        onSuccess: ({ data }) => {
          callback(data);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateStaffList = (
    staff: IUploadCentreCreateStaffList[],
    labelId: number,
    callback: (staff: Staff[]) => void
  ) => {
    return mutateUpdateStaffList(
      { labelId, data: staff },
      {
        onSuccess: ({ data }) => {
          callback(data);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateMemberList = (
    members: MemberStaffWithoutTimezoneId[],
    labelId: number,
    callback: (member: Member[]) => void
  ) => {
    return mutateUpdateMemberList(
      { labelId, data: members },
      {
        onSuccess: ({ data }) => {
          callback(data);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateServiceList = (
    service: IUploadCentreCreateServiceList[],
    labelId: number,
    callback: (service: ServiceModel[]) => void
  ) => {
    return mutateUpdateServiceList(
      { labelId, data: service },
      {
        onSuccess: ({ data }) => {
          callback(data);
        },
        onError: () => callback([]),
      }
    );
  };

  const getTimezoneId = () => {
    const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return useQuery(
      ["timezones-id", currentTimezone],
      () =>
        TimezoneService.getAll({
          name: currentTimezone,
          sortBy: JSON.stringify(["gmtOffset"]),
        }).then(({ data }) => data?.at(0)?.id),
      {
        refetchOnWindowFocus: false,
      }
    );
  };

  return {
    fleetTypes,
    isFleetTypesLoading,
    servicesCodes,
    isServicesCodesLoading,
    materialsCodes,
    isMaterialsCodesLoading,
    updateFleetList,
    isUpdateFleetListLoading,
    updateMaterialList,
    isUpdateMaterialListLoading,
    updateStaffList,
    isUpdateStaffListLoading,
    updateServiceList,
    isUpdateServiceListLoading,
    fetchUploadCentreItemsStatus,
    isUpdateMemberListLoading,
    updateMemberList,
    getTimezoneId,
    uploadItemsStatus,
    isUploadCentreItemsStatusLoading,
    warehouseList,
    isWarehouseListLoading,
    branchList,
    isBranchListLoading,
    reloadMaterialsList,
    reloadServicesList,
  };
};
